import React, {
    useEffect,
    useState,
} from "react";

import { AdminSideMenu } from "../admin/AdminMenu";

import API_URL from "../../api/API";

import {
    useAdminAuthContext,
} from "../admin/useAdminAuthContext";


// ============================================================
// TIMEZONE
// ============================================================

const TIMEZONE = "America/Chicago";


// ============================================================
// FORMAT DATE
// ============================================================

const formatDate = (dateValue) => {

    if (!dateValue) {

        return "";

    }


    try {

        return new Intl.DateTimeFormat(

            "en-US",

            {

                month: "long",

                day: "numeric",

                year: "numeric",

                timeZone: TIMEZONE,

            }

        ).format(
            new Date(dateValue)
        );

    } catch (error) {

        return "";

    }

};


// ============================================================
// FORMAT TIME
// ============================================================

const formatTime = (dateValue) => {

    if (!dateValue) {

        return "";

    }


    try {

        return new Intl.DateTimeFormat(

            "en-US",

            {

                hour: "numeric",

                minute: "2-digit",

                timeZone: TIMEZONE,

            }

        ).format(
            new Date(dateValue)
        );

    } catch (error) {

        return "";

    }

};


// ============================================================
// ADMIN CHANGE REQUEST MANAGE
// ============================================================

export const AdminChangeRequestManage = () => {


    // ========================================================
    // ADMIN AUTH
    // ========================================================

    const {
        admin,
    } = useAdminAuthContext();


    // ========================================================
    // GENERAL STATE
    // ========================================================

    const [
        changeRequests,
        setChangeRequests,
    ] = useState([]);


    const [
        isLoading,
        setIsLoading,
    ] = useState(true);


    const [
        error,
        setError,
    ] = useState("");


    const [
        isDeleting,
        setIsDeleting,
    ] = useState(false);


    // ========================================================
    // FETCH CHANGE REQUESTS
    // ========================================================

    const fetchChangeRequests = async () => {

        try {

            setIsLoading(true);

            setError("");


            const response = await fetch(

                API_URL.ADMIN_MANAGE_CHANGE_REQUEST,

                {

                    method: "GET",

                    headers: {

                        Authorization:
                            `Bearer ${admin.token}`,

                    },

                }

            );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(

                    data.error ||
                    data.message ||
                    "Failed to fetch change requests."

                );

            }


            setChangeRequests(
                data.changeRequests || []
            );


        } catch (err) {

            console.error(
                "Get Change Requests Error:",
                err
            );


            setError(
                err.message ||
                "Unable to load change requests."
            );


        } finally {

            setIsLoading(false);

        }

    };


    // ========================================================
    // DELETE CHANGE REQUEST
    // ========================================================

    const deleteChangeRequest = async (
        id
    ) => {

        try {

            setIsDeleting(true);

            setError("");


            const response = await fetch(

                API_URL.ADMIN_DELETE_CHANGE_REQUEST + id,

                {

                    method: "DELETE",

                    headers: {

                        Authorization:
                            `Bearer ${admin.token}`,

                    },

                }

            );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(

                    data.error ||
                    data.message ||
                    "Failed to delete change request."

                );

            }


            // ------------------------------------------------
            // REMOVE FROM CURRENT LIST
            // ------------------------------------------------

            setChangeRequests(
                (previousRequests) =>
                    previousRequests.filter(
                        (request) =>
                            request._id !== id
                    )
            );


        } catch (err) {

            console.error(
                "Delete Change Request Error:",
                err
            );


            setError(

                err.message ||
                "Unable to delete change request."

            );


        } finally {

            setIsDeleting(false);

        }

    };


    // ========================================================
    // DELETE CONFIRMATION
    // ========================================================

    const handleDelete = (
        id
    ) => {

        const confirmed = window.confirm(

            "Are you sure you want to delete this change request?"

        );


        if (!confirmed) {

            return;

        }


        deleteChangeRequest(id);

    };


    // ========================================================
    // GET EMPLOYEE NAME
    // ========================================================

    const getEmployeeName = (
        request
    ) => {

        const employee =
            request?.employee;


        if (!employee) {

            return "Unknown Employee";

        }


        const firstName =
            employee.firstName ||
            employee.firstname ||
            "";


        const lastName =
            employee.lastName ||
            employee.lastname ||
            "";


        const fullName =
            `${firstName} ${lastName}`.trim();


        return fullName ||
            "Unknown Employee";

    };


    // ========================================================
    // FETCH ON LOAD
    // ========================================================

    useEffect(() => {

        if (admin?.token) {

            fetchChangeRequests();

        }

    }, [admin]);


    // ========================================================
    // RENDER
    // ========================================================

    return (

        <div className="flex h-screen">


            {/* ==================================================
                ADMIN SIDE MENU
            ================================================== */}

            <AdminSideMenu />


            {/* ==================================================
                MAIN CONTENT
            ================================================== */}

            <div className="flex-1 bg-gray-100 p-6 pt-16 flex flex-col">


                {/* ==================================================
                    HEADER
                ================================================== */}

                <div className="mb-4 flex items-center justify-between">


                    <div>

                        <h1 className="text-2xl font-bold">

                            Change Requests

                        </h1>


                        <p className="mt-1 text-sm text-gray-500">

                            Review employee requests and remove them after they have been handled.

                        </p>

                    </div>


                    <div className="rounded-lg bg-white px-4 py-2 shadow-sm">

                        <span className="text-sm text-gray-500">

                            Outstanding:

                        </span>


                        <span className="ml-2 text-lg font-semibold text-gray-800">

                            {changeRequests.length}

                        </span>

                    </div>

                </div>


                {/* ==================================================
                    ERROR
                ================================================== */}

                {error && (

                    <div className="mb-4 rounded bg-red-100 p-3 text-red-600">

                        {error}

                    </div>

                )}


                {/* ==================================================
                    CHANGE REQUEST TABLE
                ================================================== */}

                <div className="flex-1 overflow-auto rounded border bg-white shadow">


                    <table className="w-full table-auto">


                        {/* ==================================================
                            TABLE HEADER
                        ================================================== */}

                        <thead className="sticky top-0 bg-gray-200">


                            <tr>


                                <th className="px-4 py-3 text-left">

                                    Employee

                                </th>


                                <th className="px-4 py-3 text-left">

                                    Request

                                </th>


                                <th className="px-4 py-3 text-left whitespace-nowrap">

                                    Submitted

                                </th>


                                <th className="px-4 py-3 text-left">

                                    Action

                                </th>


                            </tr>


                        </thead>


                        {/* ==================================================
                            TABLE BODY
                        ================================================== */}

                        <tbody>


                            {isLoading ? (

                                <tr>

                                    <td
                                        colSpan="4"
                                        className="py-8 text-center"
                                    >

                                        <p className="text-blue-600">

                                            Loading change requests...

                                        </p>

                                    </td>

                                </tr>


                            ) : changeRequests.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="4"
                                        className="py-12 text-center"
                                    >

                                        <div>

                                            <p className="text-lg font-semibold text-gray-700">

                                                No change requests

                                            </p>


                                            <p className="mt-1 text-sm text-gray-500">

                                                There are currently no outstanding employee requests.

                                            </p>

                                        </div>

                                    </td>

                                </tr>


                            ) : (

                                changeRequests.map(
                                    (
                                        request,
                                        index
                                    ) => (

                                        <tr
                                            key={
                                                request._id
                                            }
                                            className={

                                                index % 2 === 0

                                                    ? "bg-white border-b border-gray-200"

                                                    : "bg-gray-50 border-b border-gray-200"

                                            }
                                        >


                                            {/* ==================================
                                                EMPLOYEE
                                            ================================== */}

                                            <td className="px-4 py-4 align-top">


                                                <p className="font-semibold text-gray-800">

                                                    {
                                                        getEmployeeName(
                                                            request
                                                        )
                                                    }

                                                </p>


                                            </td>


                                            {/* ==================================
                                                REQUEST MESSAGE
                                            ================================== */}

                                            <td className="px-4 py-4 align-top">


                                                <div className="max-w-[650px]">

                                                    <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-gray-700">

                                                        {
                                                            request.message
                                                        }

                                                    </p>

                                                </div>


                                            </td>


                                            {/* ==================================
                                                SUBMITTED
                                            ================================== */}

                                            <td className="px-4 py-4 align-top whitespace-nowrap">


                                                <p className="text-sm font-medium text-gray-700">

                                                    {
                                                        formatDate(
                                                            request.createdAt
                                                        )
                                                    }

                                                </p>


                                                <p className="mt-1 text-xs text-gray-500">

                                                    {
                                                        formatTime(
                                                            request.createdAt
                                                        )
                                                    }

                                                </p>


                                            </td>


                                            {/* ==================================
                                                ACTION
                                            ================================== */}

                                            <td className="px-4 py-4 align-top">


                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleDelete(
                                                            request._id
                                                        )
                                                    }
                                                    disabled={
                                                        isDeleting
                                                    }
                                                    className="
                                                        rounded
                                                        bg-red-500
                                                        px-3
                                                        py-2
                                                        text-sm
                                                        font-medium
                                                        text-white
                                                        transition
                                                        hover:bg-red-600
                                                        disabled:cursor-not-allowed
                                                        disabled:opacity-50
                                                    "
                                                >

                                                    Delete

                                                </button>


                                            </td>


                                        </tr>

                                    )

                                )

                            )}


                        </tbody>


                    </table>


                </div>


            </div>


        </div>

    );

};